import { Alert } from '@/global/components/Alert'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import IconeAjuda from '@/global/components/IconeAjuda'
import Grid from '@/global/components/ui/Grid'
import { cn } from '@/lib/utils'
import { FormField, FormsProps } from 'global/components/Form'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { TabelaHorarios } from 'service/api'
import { useCanSubmit } from '../context/CanSubmitContext'
import { camposIgnorados, tipoTolerancia } from '../utils/tipo-tolerancia'

export function ToleranciasForm() {
    const { setValue, watch, formState } = useFormContext<TabelaHorarios>()
    const { setCanSubmit } = useCanSubmit()
    const { errors } = formState

    /** Tolerância é informada em minutos: mantém no campo apenas dígitos */
    function onChange(field: any) {
        return function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
            const limpo = e.target.value.replace(/\D/g, '')

            if (limpo !== e.target.value) setValue(field, limpo)
        }
    }

    const defaultProps: Partial<FormsProps> = {
        multiline: false,
        size: 'small' as const,
        unHelperText: true,
        type: 'number',
        placeholder: '0',
        required: true,
        onFocus: () => setCanSubmit(false),
        onBlur: () => setCanSubmit(true),
    }

    const [tipo, turno, dia] = watch([
        'tipo',
        'tolerancias.toleranciaTurno',
        'tolerancias.toleranciaDia',
    ])

    const disabled = useMemo(
        () =>
            camposIgnorados[
                tipoTolerancia({
                    tipo,
                    tolerancias: {
                        toleranciaTurno: turno,
                        toleranciaDia: dia,
                    },
                })
            ],
        [tipo, turno, dia],
    )

    // Verificar se há erro relacionado a tolerâncias
    const erroTolerancia =
        errors.tolerancias?.toleranciaDia?.message ||
        errors.tolerancias?.toleranciaTurno?.message ||
        errors.tolerancias?.entradaAntes?.message ||
        errors.tolerancias?.entradaDepois?.message ||
        errors.tolerancias?.saidaAntes?.message ||
        errors.tolerancias?.saidaDepois?.message ||
        errors.tolerancias?.root?.message

    return (
        <Grid container>
            <Grid item xs={12} className="mb-4">
                <HorizontalDivider>
                    <span className="inline-flex items-center text-base">
                        Tolerâncias{' '}
                        <IconeAjuda>
                            Tolerâncias podem ser definidas por registro, dia ou
                            turno.
                            <br />
                            <br />
                            Caso opte por tolerâncias por dia ou turno, as
                            demais opções não serão consideradas.
                            <br />
                            <br />
                            Opções com valores{' '}
                            <strong className="line-through">riscados</strong>{' '}
                            signifcam que estão sendo ignorados e outra
                            configuração está sendo utilizada.
                            <br />
                            <br />
                            Você também pode alterar a configuração do
                            funcionário para utilizar a tolerância CLT, nesse
                            caso, o sistema vai considerar automaticamente a
                            tolerância padrão de 5 minutos para cada registro
                            com um máximo de 10 minutos por dia.
                        </IconeAjuda>
                    </span>
                </HorizontalDivider>
                <div className="mt-2 flex select-none flex-col gap-1 text-xs">
                    <span>
                        Minutos que o registro pode variar do horário da tabela
                        sem gerar falta nem hora extra.
                    </span>
                    <span>
                        Exemplo: com <b>5</b> em <b>Entrada antes</b>, quem tem
                        entrada às 08:00 pode registrar às 07:55 sem gerar horas
                        extras.
                    </span>
                </div>
            </Grid>
            <Grid item xs={2}>
                <FormField
                    {...defaultProps}
                    name="tolerancias.entradaAntes"
                    onChange={onChange('tolerancias.entradaAntes')}
                    label={
                        <span
                            className={cn({
                                'line-through': disabled.includes('registros'),
                            })}
                        >
                            De entrada antes
                        </span>
                    }
                    className={cn({
                        'line-through': disabled.includes('registros'),
                    })}
                />
            </Grid>
            <Grid item xs={2}>
                <FormField
                    {...defaultProps}
                    name="tolerancias.entradaDepois"
                    onChange={onChange('tolerancias.entradaDepois')}
                    label={
                        <span
                            className={cn({
                                'line-through': disabled.includes('registros'),
                            })}
                        >
                            De entrada depois
                        </span>
                    }
                    className={cn({
                        'line-through': disabled.includes('registros'),
                    })}
                />
            </Grid>
            <Grid item xs={2}>
                <FormField
                    {...defaultProps}
                    name="tolerancias.saidaAntes"
                    onChange={onChange('tolerancias.saidaAntes')}
                    label={
                        <span
                            className={cn({
                                'line-through': disabled.includes('registros'),
                            })}
                        >
                            De saída antes
                        </span>
                    }
                    className={cn({
                        'line-through': disabled.includes('registros'),
                    })}
                />
            </Grid>
            <Grid item xs={2}>
                <FormField
                    {...defaultProps}
                    name="tolerancias.saidaDepois"
                    onChange={onChange('tolerancias.saidaDepois')}
                    label={
                        <span
                            className={cn({
                                'line-through': disabled.includes('registros'),
                            })}
                        >
                            De saída depois
                        </span>
                    }
                    className={cn({
                        'line-through': disabled.includes('registros'),
                    })}
                />
            </Grid>
            <Grid item xs={2}>
                <FormField
                    {...defaultProps}
                    name="tolerancias.toleranciaTurno"
                    onChange={onChange('tolerancias.toleranciaTurno')}
                    label={
                        <span
                            className={cn({
                                'line-through': disabled.includes('turno'),
                            })}
                        >
                            Por turno
                        </span>
                    }
                    className={cn({
                        'line-through': disabled.includes('turno'),
                    })}
                />
            </Grid>
            <Grid item xs={2}>
                <FormField
                    {...defaultProps}
                    name="tolerancias.toleranciaDia"
                    onChange={onChange('tolerancias.toleranciaDia')}
                    label={
                        <span
                            className={cn({
                                'line-through': disabled.includes('dia'),
                            })}
                        >
                            Por dia
                        </span>
                    }
                    className={cn({
                        'line-through': disabled.includes('dia'),
                    })}
                />
            </Grid>
            {erroTolerancia && (
                <Grid item xs={12} className="my-2">
                    <Alert severity="error">{erroTolerancia}</Alert>
                </Grid>
            )}
        </Grid>
    )
}
